import md5 from 'md5';
import { crossHash, requestApiV2 } from '../../restricted/broadcast/broadcastApiV2Auth';
import {
  BROADCAST_SECURITY_CROSS, PI_ASSISTANT_AGENTS, PI_ASSISTANT_ASK, PI_ASSISTANT_FEEDBACK, PI_ASSISTANT_USAGE, PI_ASSISTANT_USAGE_DETAIL,
  PI_CATALOG_FEATURES, PI_COMPANIES, PI_COMPANY_DEMO, PI_INSIGHT_ACK, PI_REVENUE_OPPORTUNITY_STATUS, PI_REVENUE_STATUS, PI_STATUS,
} from '../../../../urlConfig';
import { describeError, errorMessage, normalizeEnvelope } from './piEnvelope.js';
import { exportPage, exportQuery } from './piExport.js';
import {
  MENU_CACHE_KEY, REVENUE_CACHE_KEY, menuVisibleStatus, probeAnswerKnown, probeMenuStatus, readMenuCache, sessionStore, writeMenuCache,
} from './piNavigation.js';
import { withQuery } from './piQuery.js';

export { describeError, errorMessage, normalizeEnvelope };

const HIDDEN_FEATURE_CATEGORIES = ['technical'];

let featureOptionsRequest = null;

let revenueStatusesRequest = null;

const PROBES = {
  menu: { url: PI_STATUS, cacheKey: MENU_CACHE_KEY, pending: null, keepKnown: false },
  revenue: { url: PI_REVENUE_STATUS, cacheKey: REVENUE_CACHE_KEY, pending: null, keepKnown: true },
};

const bodyOf = (response) => (response ? response.data : null);

export const piGet = (url, params) =>
  requestApiV2('GET', withQuery(url, params)).then((response) => normalizeEnvelope(bodyOf(response)));

export const fetchAllRows = (path, query, pick) => piGet(path, exportQuery(query)).then((envelope) => exportPage(envelope, pick));

export const piPost = (url, body) =>
  requestApiV2('POST', url, body || {}).then((response) => normalizeEnvelope(bodyOf(response)));

export const ackInsight = (id) =>
  piPost(PI_INSIGHT_ACK(id)).then((envelope) => {
    const data = envelope.data;
    return data && typeof data.status === 'string' && data.status ? data.status : 'acked';
  });

export const changeOpportunityStatus = (id, status, reason) =>
  piPost(PI_REVENUE_OPPORTUNITY_STATUS(id), { status: status, reason: reason });

export const setCompanyDemo = (companyId, body) => requestApiV2('POST', PI_COMPANY_DEMO(companyId), body).then(bodyOf);

export const loadAssistantAgents = () => piGet(PI_ASSISTANT_AGENTS);

export const askAssistant = (body) => requestApiV2('POST', PI_ASSISTANT_ASK, body).then(bodyOf);

export const loadAssistantUsage = (params) => piGet(PI_ASSISTANT_USAGE, params);

export const loadAssistantUsageDetail = (id) => piGet(PI_ASSISTANT_USAGE_DETAIL(id));

export const sendAssistantFeedback = (id, useful, comment) =>
  requestApiV2('POST', PI_ASSISTANT_FEEDBACK(id), { useful: useful === true, comment: comment ? String(comment) : null }).then(bodyOf);

export const loadFeatureOptions = () => {
  if (!featureOptionsRequest) {
    featureOptionsRequest = piGet(PI_CATALOG_FEATURES)
      .then((envelope) => {
        const rows = Array.isArray(envelope.data) ? envelope.data : [];
        return rows
          .filter((row) => row && typeof row.featureType === 'number' && HIDDEN_FEATURE_CATEGORIES.indexOf(row.category) === -1)
          .map((row) => ({
            value: String(row.featureType),
            label: row.displayName || row.name || String(row.featureType),
            module: row.module || '',
          }))
          .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
      })
      .catch((error) => {
        featureOptionsRequest = null;
        throw error;
      });
  }
  return featureOptionsRequest;
};

export const loadRevenueStatuses = () => {
  if (!revenueStatusesRequest) {
    revenueStatusesRequest = piGet(PI_REVENUE_STATUS)
      .then((envelope) => envelope.data)
      .catch((error) => {
        revenueStatusesRequest = null;
        throw error;
      });
  }
  return revenueStatusesRequest;
};

export const searchCompanies = (term, period) => {
  const params = { search: term, page: 1, pageSize: 10 };
  if (period && period.from && period.to) {
    params.from = period.from;
    params.to = period.to;
  }
  return piGet(PI_COMPANIES, params).then((envelope) => {
    const data = envelope.data || {};
    const rows = Array.isArray(data.rows) ? data.rows : [];
    return rows
      .filter((row) => row && row.companyId)
      .map((row) => ({
        companyId: String(row.companyId),
        shardCode: row.shardCode || '',
        name: row.name || String(row.companyId),
      }));
  });
};

const AUTH_STORAGE_KEY = 'marQAdminAuthUser';

const readAuth = () => {
  try {
    const auth = JSON.parse(window.localStorage.getItem(AUTH_STORAGE_KEY));
    return auth && typeof auth === 'object' ? auth : {};
  } catch (e) {
    return {};
  }
};

const keepToken = (cross, token) => {
  try {
    const auth = JSON.parse(window.localStorage.getItem(AUTH_STORAGE_KEY));
    if (!auth || typeof auth !== 'object' || auth.crossToken !== cross) return;
    auth.apiV2Token = token;
    window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
  } catch (e) {
    return;
  }
};

const menuOwner = (auth) => (auth.crossToken ? md5(String(auth.crossToken)) : '');

const canFetch = () => typeof window !== 'undefined' && typeof window.fetch === 'function';

const statusAt = (url) => (token) => window.fetch(url, {
  method: 'GET',
  credentials: 'omit',
  cache: 'no-store',
  headers: { Accept: 'application/json', Authorization: 'Bearer ' + token },
}).then((response) => response.status);

const exchangeToken = (cross) => window.fetch(BROADCAST_SECURITY_CROSS + crossHash(cross), {
  method: 'GET',
  credentials: 'omit',
  cache: 'no-store',
  headers: { Accept: 'application/json', _tId: cross },
})
  .then((response) => (response.status === 200 ? response.json() : null))
  .then((body) => {
    const token = body && typeof body.token === 'string' ? body.token : '';
    if (token) keepToken(cross, token);
    return token;
  });

const peekProbe = (probe) => {
  try {
    return readMenuCache(sessionStore(), Date.now(), menuOwner(readAuth()), probe.cacheKey, probe.keepKnown);
  } catch (e) {
    return null;
  }
};

const resolveProbe = (probe) => {
  try {
    const auth = readAuth();
    const owner = menuOwner(auth);
    const cached = readMenuCache(sessionStore(), Date.now(), owner, probe.cacheKey, probe.keepKnown);
    if (cached !== null) return Promise.resolve(cached);
    if (!owner || !canFetch()) return Promise.resolve(false);
    if (!probe.pending) {
      probe.pending = new Promise((resolve) => {
        try {
          probeMenuStatus(auth, statusAt(probe.url), exchangeToken).then(resolve, () => resolve(0));
        } catch (e) {
          resolve(0);
        }
      }).then((status) => {
        const visible = menuVisibleStatus(status);
        writeMenuCache(sessionStore(), visible, Date.now(), owner, probe.cacheKey, probe.keepKnown && probeAnswerKnown(status));
        probe.pending = null;
        return visible;
      });
    }
    return probe.pending;
  } catch (e) {
    return Promise.resolve(false);
  }
};

export const peekMenuVisibility = () => peekProbe(PROBES.menu);

export const resolveMenuVisibility = () => resolveProbe(PROBES.menu);

export const peekRevenueVisibility = () => peekProbe(PROBES.revenue);

export const resolveRevenueVisibility = () => resolveProbe(PROBES.revenue);



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piApi.js